import type { Webinar } from "./webinars.js";

/**
 * Seed for the webinar INVITATION email (step 1).
 *
 * crm-api already ships a rich `webinar` campaign layout, and the dashboard
 * already ships its in-browser section builder
 * (`apps/web/src/crm/components/webinar-builder/`). Step 1 does not
 * re-implement either: it hands the campaign editor a pre-filled set of the
 * builder's OWN sections, the builder rehydrates them, renders the canonical
 * HTML, and the operator polishes and sends from the editor they already use.
 *
 * So this module produces DATA, never HTML. That is the whole point — a
 * second HTML renderer would be a third copy of the same email (the browser
 * builder and crm-api's `WebinarHtmlGeneratorService` are the first two), and
 * they would drift.
 *
 * It lives in `packages/shared` because the section shape is a contract
 * between the Webinars page and the CRM sub-app, which cannot import each
 * other's internals (see the CRM import-boundary rule in CLAUDE.md).
 */

/** Structural mirror of the builder's `WebinarSection`. Kept structural (not
 * imported) because the builder lives inside the walled CRM sub-app; the two
 * are pinned together by `webinarInvite.test.ts` + the builder's own specs. */
export interface WebinarInviteSection {
  id: string;
  type: "header" | "webinar_details" | "speakers" | "footer";
  content: Record<string, unknown>;
  backgroundColor?: string;
}

/** Default hero image. One of the builder's own stock banners — a webinar
 * setup, not the finance banner that happens to sit first in its list. */
export const DEFAULT_INVITE_HEADER_IMAGE = "/stock-images/webinar-setup.jpg";

/** "September 1, 2026" — the format the builder's date field expects (it is
 * a free-text field the operator can overwrite). */
export function inviteDateLabel(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * "3:00 PM UTC".
 *
 * Deliberately labelled UTC rather than guessing a local zone: the stored
 * instant is UTC, the recipients are worldwide, and a time silently rendered
 * in the operator's browser zone is the kind of thing that puts the wrong
 * hour in an invitation. The operator can rewrite the field.
 */
export function inviteTimeLabel(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const t = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  });
  return `${t} UTC`;
}

/** "60 minutes" / "1 hour 30 minutes" — plain enough to read in an email. */
export function inviteDurationLabel(minutes: number): string {
  if (!minutes || minutes <= 0) return "";
  if (minutes < 60) return `${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const hourPart = `${hours} hour${hours === 1 ? "" : "s"}`;
  return rest ? `${hourPart} ${rest} minutes` : hourPart;
}

/**
 * Build the builder's sections from a webinar record.
 *
 * Every field the operator already typed on the webinar (title, description,
 * date, length, join link) lands in the right place, so step 1 opens on a
 * nearly-finished email rather than the builder's generic placeholder copy.
 * Fields the webinar does not have are left EMPTY rather than filled with
 * lorem-ipsum: a blank line is obviously "fill me in", whereas plausible
 * placeholder text is what gets accidentally sent.
 */
export function buildInviteSections(webinar: Webinar): WebinarInviteSection[] {
  const description = webinar.description.trim();
  return [
    {
      id: "header",
      type: "header",
      content: {
        title: "You're Invited!",
        subtitle: "Webinar Invitation",
        overlayText: "",
        imageUrl: DEFAULT_INVITE_HEADER_IMAGE,
      },
    },
    {
      id: "webinar-details",
      type: "webinar_details",
      backgroundColor: "#ffffff",
      content: {
        title: webinar.title,
        tagline: "Join us for a live session",
        date: inviteDateLabel(webinar.scheduled_at),
        time: inviteTimeLabel(webinar.scheduled_at),
        duration: inviteDurationLabel(webinar.duration_minutes),
        location: "",
        registrationLink: webinar.registration_link,
        // The builder's description field is rich text, so wrap the plain
        // description in a paragraph. Empty stays empty.
        description: description ? `<p>${escapeHtml(description)}</p>` : "",
      },
    },
    {
      id: "speakers",
      type: "speakers",
      backgroundColor: "#f7fafc",
      content: {
        // Off by default: we do not know who is speaking, and three rows of
        // "Speaker Name / Job Title" is the most likely thing to get sent by
        // accident. The operator switches it on in the builder.
        enabled: false,
        heading: "Meet the Speakers",
        speakers: [],
      },
    },
    {
      id: "footer",
      type: "footer",
      backgroundColor: "#2d3748",
      content: {},
    },
  ];
}

/** Everything the campaign editor needs to open pre-filled for a webinar. */
export interface WebinarCampaignSeed {
  /** Which webinar this campaign belongs to, so the editor can link the
   * campaign back once its draft is first saved. */
  webinar_id: number;
  name: string;
  subject: string;
  layout: "webinar";
  sections: WebinarInviteSection[];
}

export function buildWebinarCampaignSeed(webinar: Webinar): WebinarCampaignSeed {
  return {
    webinar_id: webinar.id,
    name: `${webinar.title} - invitation`,
    subject: `You are invited: ${webinar.title}`,
    layout: "webinar",
    sections: buildInviteSections(webinar),
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
